import React, { useState, useEffect } from 'react';
import Loading from '../common/Loading';

const StaffPage = () => {
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [stores, setStores] = useState([]);
  const [editingStaff, setEditingStaff] = useState(null);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [filter, setFilter] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStore, setFilterStore] = useState('all');

  // コンポーネントマウント時にデータを取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 実際の実装ではAPIコールを行う
        // 今回はモックデータでシミュレーション
        const mockStores = generateMockStores();
        const mockStaff = generateMockStaff(mockStores);
        
        setStores(mockStores);
        setStaff(mockStaff);
        
        setLoading(false);
      } catch (error) {
        console.error('データ取得エラー:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // モック店舗データを生成
  const generateMockStores = () => {
    return [
      { id: 1, name: '本店' },
      { id: 2, name: '西口店' },
      { id: 3, name: '駅前店' }
    ];
  };

  // モックスタッフデータを生成
  const generateMockStaff = (mockStores) => {
    return [
      {
        id: 1,
        name: '高橋 真史',
        type: '社員',
        email: 'takahashi@example.com',
        phone: '090-1234-5678',
        storeId: 1,
        skills: ['バリスタ', 'レジ', '開店'],
        isActive: true,
        joiningDate: '2020-04-01'
      },
      {
        id: 2,
        name: '鈴木 健太',
        type: '社員',
        email: 'suzuki@example.com',
        phone: '090-2345-6789',
        storeId: 2,
        skills: ['キッチン', 'ホール', '閉店'],
        isActive: true,
        joiningDate: '2020-06-15'
      },
      {
        id: 3,
        name: '佐藤 明子',
        type: 'パート',
        email: 'sato@example.com',
        phone: '090-3456-7890',
        storeId: 1,
        skills: ['レジ', 'ホール'],
        isActive: true,
        joiningDate: '2021-02-10'
      },
      {
        id: 4,
        name: '田中 優',
        type: 'パート',
        email: 'tanaka@example.com',
        phone: '090-4567-8901',
        storeId: 3,
        skills: ['キッチン', 'ホール'],
        isActive: true,
        joiningDate: '2021-05-20'
      },
      {
        id: 5,
        name: '山田 花子',
        type: 'アルバイト',
        email: 'yamada@example.com',
        phone: '090-5678-9012',
        storeId: 1,
        skills: ['ホール'],
        isActive: true,
        joiningDate: '2022-01-05'
      },
      {
        id: 6,
        name: '伊藤 拓也',
        type: 'アルバイト',
        email: 'ito@example.com',
        phone: '090-6789-0123',
        storeId: 2,
        skills: ['レジ'],
        isActive: false,
        joiningDate: '2022-03-15'
      },
      {
        id: 7,
        name: '木村 誠',
        type: '単発バイト',
        email: 'kimura@example.com',
        phone: '090-7890-1234',
        storeId: 3,
        skills: ['ホール'],
        isActive: true,
        joiningDate: '2023-01-10'
      }
    ];
  };

  // 新規スタッフ追加ボタンのクリックハンドラ
  const handleAddStaff = () => {
    setEditingStaff({
      id: null,
      name: '',
      type: 'アルバイト',
      email: '',
      phone: '',
      storeId: stores.length > 0 ? stores[0].id : '',
      skills: [],
      isActive: true,
      joiningDate: new Date().toISOString().split('T')[0]
    });
    setShowStaffModal(true);
  };

  // スタッフ編集ボタンのクリックハンドラ
  const handleEditStaff = (staffMember) => {
    setEditingStaff({
      ...staffMember,
      skills: [...staffMember.skills] // 配列のコピーを作成
    });
    setShowStaffModal(true);
  };

  // スタッフ削除ボタンのクリックハンドラ
  const handleDeleteStaff = (staffId) => {
    if (window.confirm('このスタッフを削除してもよろしいですか？')) {
      // 実際の実装ではAPIコールを行う
      setStaff(prevStaff => prevStaff.filter(staff => staff.id !== staffId));
    }
  };

  // スタッフ保存ハンドラ
  const handleSaveStaff = (updatedStaff) => {
    if (updatedStaff.id) {
      // 既存スタッフの更新
      setStaff(prevStaff => 
        prevStaff.map(staff => 
          staff.id === updatedStaff.id ? updatedStaff : staff
        )
      );
    } else {
      // 新規スタッフの追加
      const newStaff = {
        ...updatedStaff,
        id: Math.max(...staff.map(s => s.id), 0) + 1
      };
      setStaff(prevStaff => [...prevStaff, newStaff]);
    }
    
    setShowStaffModal(false);
    setEditingStaff(null);
  };

  // 店舗名を取得
  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store ? store.name : '不明';
  };

  // フィルタリングされたスタッフリスト
  const filteredStaff = staff.filter(staffMember => {
    // 名前、メール、電話でのフィルタリング
    const textFilter = filter === '' || 
      staffMember.name.toLowerCase().includes(filter.toLowerCase()) ||
      staffMember.email.toLowerCase().includes(filter.toLowerCase()) ||
      staffMember.phone.includes(filter);
      
    // 雇用形態でのフィルタリング
    const typeFilter = filterType === 'all' || staffMember.type === filterType;
    
    // 店舗でのフィルタリング
    const storeFilter = filterStore === 'all' || staffMember.storeId.toString() === filterStore;
    
    return textFilter && typeFilter && storeFilter;
  });

  // スタッフモーダル
  const StaffModal = () => {
    const [staffData, setStaffData] = useState(editingStaff);
    const [skillInput, setSkillInput] = useState('');
    
    const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;
      
      if (name === 'storeId') {
        setStaffData({
          ...staffData,
          storeId: parseInt(value, 10)
        });
      } else {
        setStaffData({
          ...staffData,
          [name]: type === 'checkbox' ? checked : value
        });
      }
    };
    
    const handleAddSkill = () => {
      if (skillInput.trim() && !staffData.skills.includes(skillInput.trim())) {
        setStaffData({
          ...staffData,
          skills: [...staffData.skills, skillInput.trim()]
        });
        setSkillInput('');
      }
    };
    
    const handleRemoveSkill = (skill) => {
      setStaffData({
        ...staffData,
        skills: staffData.skills.filter(s => s !== skill)
      });
    };
    
    const handleSubmit = (e) => {
      e.preventDefault();
      handleSaveStaff(staffData);
    };
    
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3>{editingStaff.id ? 'スタッフを編集' : '新規スタッフを追加'}</h3>
            <button 
              className="modal-close-btn"
              onClick={() => setShowStaffModal(false)}
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">名前 <span className="required">*</span></label>
              <input 
                type="text" 
                id="name" 
                name="name"
                value={staffData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="type">雇用形態 <span className="required">*</span></label>
              <select 
                id="type" 
                name="type"
                value={staffData.type}
                onChange={handleInputChange}
                required
              >
                <option value="社員">社員</option>
                <option value="パート">パート</option>
                <option value="アルバイト">アルバイト</option>
                <option value="単発バイト">単発バイト</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="email">メールアドレス <span className="required">*</span></label>
              <input 
                type="email" 
                id="email" 
                name="email"
                value={staffData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">電話番号 <span className="required">*</span></label>
              <input 
                type="tel" 
                id="phone" 
                name="phone"
                value={staffData.phone}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="storeId">所属店舗 <span className="required">*</span></label>
              <select 
                id="storeId" 
                name="storeId"
                value={staffData.storeId}
                onChange={handleInputChange}
                required
              >
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="joiningDate">入社日 <span className="required">*</span></label>
              <input 
                type="date" 
                id="joiningDate" 
                name="joiningDate"
                value={staffData.joiningDate}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label>スキル</label>
              <div className="skills-input-container">
                <input 
                  type="text" 
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  placeholder="スキルを入力..."
                />
                <button 
                  type="button" 
                  className="add-skill-btn"
                  onClick={handleAddSkill}
                >
                  追加
                </button>
              </div>
              
              <div className="skills-list">
                {staffData.skills.map(skill => (
                  <div key={skill} className="skill-tag">
                    <span>{skill}</span>
                    <button 
                      type="button"
                      className="remove-skill-btn"
                      onClick={() => handleRemoveSkill(skill)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="form-group checkbox-group">
              <label>
                <input 
                  type="checkbox" 
                  name="isActive"
                  checked={staffData.isActive}
                  onChange={handleInputChange}
                />
                アクティブ（在籍中）
              </label>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="save-btn">保存</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="staff-page">
      <h2>スタッフ管理</h2>
      <p>スタッフ情報の登録、編集、削除ができます</p>
      
      <div className="filters-container">
        <div className="search-box">
          <input 
            type="text"
            placeholder="名前、メール、電話で検索..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        
        <div className="filter-selects">
          <div className="filter-group">
            <label>雇用形態:</label>
            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">すべて</option>
              <option value="社員">社員</option>
              <option value="パート">パート</option>
              <option value="アルバイト">アルバイト</option>
              <option value="単発バイト">単発バイト</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>店舗:</label>
            <select 
              value={filterStore}
              onChange={(e) => setFilterStore(e.target.value)}
            >
              <option value="all">すべて</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          </div>
        </div>
        
        <button 
          className="add-staff-btn"
          onClick={handleAddStaff}
        >
          新規スタッフを追加
        </button>
      </div>
      
      <div className="staff-list">
        <table className="staff-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>名前</th>
              <th>雇用形態</th>
              <th>メールアドレス</th>
              <th>電話番号</th>
              <th>所属店舗</th>
              <th>スキル</th>
              <th>ステータス</th>
              <th>入社日</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredStaff.length > 0 ? (
              filteredStaff.map(staffMember => (
                <tr key={staffMember.id}>
                  <td>{staffMember.id}</td>
                  <td>{staffMember.name}</td>
                  <td>{staffMember.type}</td>
                  <td>{staffMember.email}</td>
                  <td>{staffMember.phone}</td>
                  <td>{getStoreName(staffMember.storeId)}</td>
                  <td>
                    <div className="skills-container">
                      {staffMember.skills.map(skill => (
                        <span key={skill} className="skill-badge">{skill}</span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${staffMember.isActive ? 'active' : 'inactive'}`}>
                      {staffMember.isActive ? '在籍中' : '退職'}
                    </span>
                  </td>
                  <td>{staffMember.joiningDate}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="edit-btn"
                        onClick={() => handleEditStaff(staffMember)}
                      >
                        編集
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => handleDeleteStaff(staffMember.id)}
                      >
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="no-results">該当するスタッフがいません</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {showStaffModal && <StaffModal />}
    </div>
  );
};

export default StaffPage;
